import { AssessmentDefinition, CalculationType, InputType, SideType } from '../models/assessment-definition.model';

export const FGADefinition: AssessmentDefinition = {
  id: 'fga',
  version: '1.0',
  name: 'Functional Gait Assessment',
  shortName: 'FGA',
  description:
    'Das Functional Gait Assessment ist ein standardisierter Test zur Bewertung des Gleichgewichts während verschiedener Gangaufgaben, insbesondere bei älteren Menschen und bei Personen mit neurologischen Erkrankungen.',
  guide: `<p>Das Functional Gait Assessment besteht aus 10 Aufgaben, die jeweils auf einer Skala von 0-3 bewertet werden. Die maximal erreichbare Punktzahl beträgt 30 Punkte.</p>
    <p><strong>Benötigte Materialien:</strong></p>
    <ul>
      <li>Eine etwa 6 Meter lange Gehstrecke</li>
      <li>Stoppuhr</li>
      <li>Ein Schaumstoffkissen (nur für Aufgabe 10)</li>
      <li>Ein Schuhkarton oder ähnliches als Hindernis (ca. 20 cm hoch)</li>
      <li>Eine Treppe mit Handlauf</li>
    </ul>
    <p><strong>Interpretation der Ergebnisse:</strong></p>
    <ul>
      <li>Eine höhere Punktzahl weist auf ein besseres Gleichgewicht hin.</li>
      <li>Eine Punktzahl von ≤22/30 deutet auf ein erhöhtes Sturzrisiko hin.</li>
    </ul>`,
  groups: [
    {
      id: 'fga-tasks',
      title: 'Gangaufgaben',
      description: 'Die 10 Aufgaben des Functional Gait Assessment',
      tasks: [
        {
          id: 'fga-task-1',
          title: '1. Gehen auf ebener Oberfläche',
          description: 'Der Patient soll in normaler Geschwindigkeit über eine Strecke von 6 Metern gehen.',
          guide: `<p><strong>Anleitung:</strong> Gehen Sie in Ihrem normalen Tempo von hier bis zur Markierung (6 Meter entfernt).</p>
            <p><strong>Bewertungsschlüssel:</strong></p>
            <ul>
              <li>3: Geht normal 6 Meter, keine Hilfsmittel, gute Geschwindigkeit, keine Hinweise auf Gleichgewichtsprobleme</li>
              <li>2: Leichte Gleichgewichtsprobleme, geringe Gangabweichungen, keine Hilfsmittel nötig</li>
              <li>1: Geht 6 Meter, benötigt Hilfsmittel, langsame Geschwindigkeit</li>
              <li>0: Kann nicht 6 Meter ohne Hilfe gehen, schwere Gangabweichungen oder Gleichgewichtsprobleme</li>
            </ul>`,
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'Kann nicht 6 Meter ohne Hilfe gehen, schwere Gangabweichungen oder Gleichgewichtsprobleme',
            },
            { value: 1, label: '1', description: 'Geht 6 Meter, benötigt Hilfsmittel, langsame Geschwindigkeit' },
            {
              value: 2,
              label: '2',
              description: 'Leichte Gleichgewichtsprobleme, geringe Gangabweichungen, keine Hilfsmittel nötig',
            },
            {
              value: 3,
              label: '3',
              description:
                'Geht normal 6 Meter, keine Hilfsmittel, gute Geschwindigkeit, keine Hinweise auf Gleichgewichtsprobleme',
            },
          ],
          minValue: 0,
          maxValue: 3,
          defaultValue: null,
          isRequired: true,
          icfCategories: [
            { code: 'd450', description: 'Gehen' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
          ],
        },
        {
          id: 'fga-task-2',
          title: '2. Ändern der Gehgeschwindigkeit',
          description:
            'Der Patient beginnt in normaler Geschwindigkeit, wechselt dann zu schnellem Gehen und anschließend zu langsamem Gehen.',
          guide: `<p><strong>Anleitung:</strong> Beginnen Sie in Ihrem normalen Tempo zu gehen (1,5 Meter). Wenn ich "schnell" sage, gehen Sie so schnell wie möglich (1,5 Meter). Wenn ich "langsam" sage, gehen Sie sehr langsam (1,5 Meter).</p>
            <p><strong>Bewertungsschlüssel:</strong></p>
            <ul>
              <li>3: Kann Gehgeschwindigkeit reibungslos ändern ohne Gleichgewichtsprobleme, deutliche Änderung der Gehgeschwindigkeit</li>
              <li>2: Kann Gehgeschwindigkeit ändern, geringe Hinweise auf Gleichgewichtsprobleme, leichte Änderung der Gehgeschwindigkeit</li>
              <li>1: Änderung der Geschwindigkeit mit deutlichen Gangunregelmäßigkeiten oder Hilfsmittel erforderlich</li>
              <li>0: Kann Geschwindigkeit nicht ändern oder verliert das Gleichgewicht beim Versuch</li>
            </ul>`,
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'Kann Geschwindigkeit nicht ändern oder verliert das Gleichgewicht beim Versuch',
            },
            {
              value: 1,
              label: '1',
              description:
                'Änderung der Geschwindigkeit mit deutlichen Gangunregelmäßigkeiten oder Hilfsmittel erforderlich',
            },
            {
              value: 2,
              label: '2',
              description:
                'Kann Gehgeschwindigkeit ändern, geringe Hinweise auf Gleichgewichtsprobleme, leichte Änderung der Gehgeschwindigkeit',
            },
            {
              value: 3,
              label: '3',
              description:
                'Kann Gehgeschwindigkeit reibungslos ändern ohne Gleichgewichtsprobleme, deutliche Änderung der Gehgeschwindigkeit',
            },
          ],
          minValue: 0,
          maxValue: 3,
          defaultValue: null,
          isRequired: true,
          icfCategories: [
            { code: 'd450', description: 'Gehen' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
          ],
        },
        {
          id: 'fga-task-3',
          title: '3. Gehen mit horizontalen Kopfdrehungen',
          description: 'Der Patient dreht den Kopf nach rechts und links während des Gehens.',
          guide: `<p><strong>Anleitung:</strong> Beginnen Sie in Ihrem normalen Tempo zu gehen. Wenn ich "rechts" sage, drehen Sie den Kopf nach rechts und schauen gerade aus. Wenn ich "links" sage, drehen Sie den Kopf nach links und schauen gerade aus. Halten Sie den Kopf dabei gerade und drehen Sie ihn nicht mit dem Körper.</p>
            <p><strong>Bewertungsschlüssel:</strong></p>
            <ul>
              <li>3: Führt Kopfdrehungen reibungslos durch ohne Änderung der Gangart</li>
              <li>2: Leichte Beeinträchtigung der Gangart, minimale Verlangsamung beim Gehen oder geringe Gleichgewichtsprobleme</li>
              <li>1: Mäßige Gangbeeinträchtigung, verlangsamt beim Gehen, Schwanken, erholt sich bei Gleichgewichtsstörungen</li>
              <li>0: Schwere Beeinträchtigung, kann Kopf nicht drehen beim Gehen, benötigt Unterstützung</li>
            </ul>`,
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'Schwere Beeinträchtigung, kann Kopf nicht drehen beim Gehen, benötigt Unterstützung',
            },
            {
              value: 1,
              label: '1',
              description:
                'Mäßige Gangbeeinträchtigung, verlangsamt beim Gehen, Schwanken, erholt sich bei Gleichgewichtsstörungen',
            },
            {
              value: 2,
              label: '2',
              description:
                'Leichte Beeinträchtigung der Gangart, minimale Verlangsamung beim Gehen oder geringe Gleichgewichtsprobleme',
            },
            {
              value: 3,
              label: '3',
              description: 'Führt Kopfdrehungen reibungslos durch ohne Änderung der Gangart',
            },
          ],
          minValue: 0,
          maxValue: 3,
          defaultValue: null,
          isRequired: true,
          icfCategories: [
            { code: 'd450', description: 'Gehen' },
            { code: 'b235', description: 'Vestibulärfunktion' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
          ],
        },
        {
          id: 'fga-task-4',
          title: '4. Gehen mit vertikalen Kopfbewegungen',
          description: 'Der Patient bewegt den Kopf nach oben und unten während des Gehens.',
          guide: `<p><strong>Anleitung:</strong> Beginnen Sie in Ihrem normalen Tempo zu gehen. Wenn ich "hoch" sage, schauen Sie nach oben. Wenn ich "runter" sage, schauen Sie nach unten. Halten Sie den Kopf dabei gerade in Bezug auf den Körper.</p>
            <p><strong>Bewertungsschlüssel:</strong></p>
            <ul>
              <li>3: Führt Kopfbewegungen reibungslos durch ohne Änderung der Gangart</li>
              <li>2: Minimale Beeinträchtigung der Gangart, geringe Gleichgewichtsprobleme</li>
              <li>1: Mäßige Gangbeeinträchtigung, verlangsamt beim Gehen, Schwanken, erholt sich bei Gleichgewichtsstörungen</li>
              <li>0: Schwere Beeinträchtigung, kann Kopf nicht bewegen beim Gehen, benötigt Unterstützung</li>
            </ul>`,
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'Schwere Beeinträchtigung, kann Kopf nicht bewegen beim Gehen, benötigt Unterstützung',
            },
            {
              value: 1,
              label: '1',
              description:
                'Mäßige Gangbeeinträchtigung, verlangsamt beim Gehen, Schwanken, erholt sich bei Gleichgewichtsstörungen',
            },
            {
              value: 2,
              label: '2',
              description: 'Minimale Beeinträchtigung der Gangart, geringe Gleichgewichtsprobleme',
            },
            {
              value: 3,
              label: '3',
              description: 'Führt Kopfbewegungen reibungslos durch ohne Änderung der Gangart',
            },
          ],
          minValue: 0,
          maxValue: 3,
          defaultValue: null,
          isRequired: true,
          icfCategories: [
            { code: 'd450', description: 'Gehen' },
            { code: 'b235', description: 'Vestibulärfunktion' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
          ],
        },
        {
          id: 'fga-task-5',
          title: '5. Gehen und Drehung um die eigene Achse',
          description: 'Der Patient geht und dreht sich dann schnell um die eigene Achse.',
          guide: `<p><strong>Anleitung:</strong> Beginnen Sie in Ihrem normalen Tempo zu gehen. Wenn ich "umdrehen" sage, drehen Sie sich schnell um und bleiben in der entgegengesetzten Richtung stehen.</p>
            <p><strong>Bewertungsschlüssel:</strong></p>
            <ul>
              <li>3: Dreht sich schnell (≤3 Schritte) und sicher um, ohne Gleichgewichtsverlust</li>
              <li>2: Dreht sich langsam (>3 Schritte), aber sicher um, ohne Gleichgewichtsverlust</li>
              <li>1: Dreht sich langsam um, benötigt Hinweise, leichte Gleichgewichtsprobleme</li>
              <li>0: Kann sich nicht sicher umdrehen, benötigt Unterstützung</li>
            </ul>`,
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'Kann sich nicht sicher umdrehen, benötigt Unterstützung' },
            {
              value: 1,
              label: '1',
              description: 'Dreht sich langsam um, benötigt Hinweise, leichte Gleichgewichtsprobleme',
            },
            {
              value: 2,
              label: '2',
              description: 'Dreht sich langsam (>3 Schritte), aber sicher um, ohne Gleichgewichtsverlust',
            },
            {
              value: 3,
              label: '3',
              description: 'Dreht sich schnell (≤3 Schritte) und sicher um, ohne Gleichgewichtsverlust',
            },
          ],
          minValue: 0,
          maxValue: 3,
          defaultValue: null,
          isRequired: true,
          icfCategories: [
            { code: 'd450', description: 'Gehen' },
            { code: 'd455', description: 'Sich auf andere Weise fortbewegen' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
          ],
        },
        {
          id: 'fga-task-6',
          title: '6. Übersteigen eines Hindernisses',
          description: 'Der Patient übersteigt ein Hindernis während des Gehens.',
          guide: `<p><strong>Anleitung:</strong> Beginnen Sie in Ihrem normalen Tempo zu gehen. Wenn Sie zu dem Schuhkarton kommen, steigen Sie darüber, nicht darum herum, und gehen Sie weiter.</p>
            <p><strong>Bewertungsschlüssel:</strong></p>
            <ul>
              <li>3: Kann Hindernis überwinden ohne Gangtempo zu ändern, keine Anzeichen von Gleichgewichtsproblemen</li>
              <li>2: Kann Hindernis überwinden, muss aber Tempo verlangsamen und Schritte anpassen</li>
              <li>1: Kann Hindernis überwinden, muss aber anhalten und dann übersteigen, benötigt verbale Hinweise</li>
              <li>0: Kann Hindernis nicht sicher überwinden, benötigt Hilfe</li>
            </ul>`,
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'Kann Hindernis nicht sicher überwinden, benötigt Hilfe' },
            {
              value: 1,
              label: '1',
              description:
                'Kann Hindernis überwinden, muss aber anhalten und dann übersteigen, benötigt verbale Hinweise',
            },
            {
              value: 2,
              label: '2',
              description: 'Kann Hindernis überwinden, muss aber Tempo verlangsamen und Schritte anpassen',
            },
            {
              value: 3,
              label: '3',
              description:
                'Kann Hindernis überwinden ohne Gangtempo zu ändern, keine Anzeichen von Gleichgewichtsproblemen',
            },
          ],
          minValue: 0,
          maxValue: 3,
          defaultValue: null,
          isRequired: true,
          icfCategories: [
            { code: 'd450', description: 'Gehen' },
            { code: 'd455', description: 'Sich auf andere Weise fortbewegen' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
          ],
        },
        {
          id: 'fga-task-7',
          title: '7. Gehen mit schmaler Unterstützungsfläche',
          description: 'Der Patient geht mit eng nebeneinander gesetzten Füßen.',
          guide: `<p><strong>Anleitung:</strong> Gehen Sie mit den Füßen dicht hintereinander, sodass die Ferse des vorderen Fußes die Zehen des hinteren Fußes berührt. Ich werde Ihre Schritte zählen. Schauen Sie nach vorn.</p>
            <p><strong>Bewertungsschlüssel:</strong></p>
            <ul>
              <li>3: Kann unabhängig und reibungslos 10 Schritte im Tandemstand gehen</li>
              <li>2: Kann 7-9 Schritte im Tandemstand gehen</li>
              <li>1: Kann 4-6 Schritte im Tandemstand gehen</li>
              <li>0: Kann weniger als 4 Schritte im Tandemstand gehen oder benötigt Hilfe</li>
            </ul>`,
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'Kann weniger als 4 Schritte im Tandemstand gehen oder benötigt Hilfe',
            },
            { value: 1, label: '1', description: 'Kann 4-6 Schritte im Tandemstand gehen' },
            { value: 2, label: '2', description: 'Kann 7-9 Schritte im Tandemstand gehen' },
            {
              value: 3,
              label: '3',
              description: 'Kann unabhängig und reibungslos 10 Schritte im Tandemstand gehen',
            },
          ],
          minValue: 0,
          maxValue: 3,
          defaultValue: null,
          isRequired: true,
          icfCategories: [
            { code: 'd450', description: 'Gehen' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
          ],
        },
        {
          id: 'fga-task-8',
          title: '8. Gehen mit geschlossenen Augen',
          description: 'Der Patient geht mit geschlossenen Augen.',
          guide: `<p><strong>Anleitung:</strong> Schließen Sie Ihre Augen und gehen Sie in Ihrem normalen Tempo bis zur nächsten Markierung (6 Meter entfernt).</p>
            <p><strong>Bewertungsschlüssel:</strong></p>
            <ul>
              <li>3: Geht normal 6 Meter, keine Hilfsmittel, gute Geschwindigkeit, keine Abweichungen</li>
              <li>2: Geht leicht unsicher, leichte Abweichungen vom geraden Weg, nutzt keine Hilfsmittel</li>
              <li>1: Geht unsicher, benötigt Hilfsmittel, deutliche Abweichungen vom geraden Weg</li>
              <li>0: Kann nicht 6 Meter ohne Hilfe gehen, schwere Gangabweichungen oder Gleichgewichtsprobleme</li>
            </ul>`,
          inputType: InputType.RadioGroup,
          options: [
            {
              value: 0,
              label: '0',
              description: 'Kann nicht 6 Meter ohne Hilfe gehen, schwere Gangabweichungen oder Gleichgewichtsprobleme',
            },
            {
              value: 1,
              label: '1',
              description: 'Geht unsicher, benötigt Hilfsmittel, deutliche Abweichungen vom geraden Weg',
            },
            {
              value: 2,
              label: '2',
              description: 'Geht leicht unsicher, leichte Abweichungen vom geraden Weg, nutzt keine Hilfsmittel',
            },
            {
              value: 3,
              label: '3',
              description: 'Geht normal 6 Meter, keine Hilfsmittel, gute Geschwindigkeit, keine Abweichungen',
            },
          ],
          minValue: 0,
          maxValue: 3,
          defaultValue: null,
          isRequired: true,
          icfCategories: [
            { code: 'd450', description: 'Gehen' },
            { code: 'b235', description: 'Vestibulärfunktion' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
          ],
        },
        {
          id: 'fga-task-9',
          title: '9. Rückwärtsgehen',
          description: 'Der Patient geht 6 Meter rückwärts.',
          guide: `<p><strong>Anleitung:</strong> Gehen Sie rückwärts bis zur Markierung (6 Meter), ohne zur Seite zu schauen. Schauen Sie aus Sicherheitsgründen gelegentlich über Ihre Schulter.</p>
            <p><strong>Bewertungsschlüssel:</strong></p>
            <ul>
              <li>3: Geht 6 Meter rückwärts, keine Hilfsmittel, gute Geschwindigkeit, leichte Abweichungen</li>
              <li>2: Geht 6 Meter rückwärts, langsam oder geringe Abweichungen vom geraden Weg</li>
              <li>1: Geht 6 Meter rückwärts, benötigt Hilfsmittel oder deutliche Abweichungen</li>
              <li>0: Kann nicht 6 Meter rückwärts gehen oder benötigt Hilfe</li>
            </ul>`,
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'Kann nicht 6 Meter rückwärts gehen oder benötigt Hilfe' },
            {
              value: 1,
              label: '1',
              description: 'Geht 6 Meter rückwärts, benötigt Hilfsmittel oder deutliche Abweichungen',
            },
            {
              value: 2,
              label: '2',
              description: 'Geht 6 Meter rückwärts, langsam oder geringe Abweichungen vom geraden Weg',
            },
            {
              value: 3,
              label: '3',
              description: 'Geht 6 Meter rückwärts, keine Hilfsmittel, gute Geschwindigkeit, leichte Abweichungen',
            },
          ],
          minValue: 0,
          maxValue: 3,
          defaultValue: null,
          isRequired: true,
          icfCategories: [
            { code: 'd450', description: 'Gehen' },
            { code: 'd455', description: 'Sich auf andere Weise fortbewegen' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
          ],
        },
        {
          id: 'fga-task-10',
          title: '10. Treppensteigen',
          description: 'Der Patient steigt Treppen hoch und runter ohne Festhalten am Geländer.',
          guide: `<p><strong>Anleitung:</strong> Steigen Sie die Treppe wie zu Hause hoch und wieder herunter. Versuchen Sie, das Geländer nicht zu benutzen, wenn möglich.</p>
            <p><strong>Bewertungsschlüssel:</strong></p>
            <ul>
              <li>3: Steigt abwechselnd Treppen, ohne Geländer zu benutzen</li>
              <li>2: Steigt abwechselnd Treppen, muss aber Geländer benutzen</li>
              <li>1: Setzt beide Füße auf jede Stufe, muss Geländer benutzen</li>
              <li>0: Kann Treppen nicht sicher besteigen</li>
            </ul>`,
          inputType: InputType.RadioGroup,
          options: [
            { value: 0, label: '0', description: 'Kann Treppen nicht sicher besteigen' },
            { value: 1, label: '1', description: 'Setzt beide Füße auf jede Stufe, muss Geländer benutzen' },
            { value: 2, label: '2', description: 'Steigt abwechselnd Treppen, muss aber Geländer benutzen' },
            { value: 3, label: '3', description: 'Steigt abwechselnd Treppen, ohne Geländer zu benutzen' },
          ],
          minValue: 0,
          maxValue: 3,
          defaultValue: null,
          isRequired: true,
          icfCategories: [
            { code: 'd455', description: 'Sich auf andere Weise fortbewegen' },
            { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
          ],
        },
      ],
      icfCategories: [
        { code: 'd450', description: 'Gehen' },
        { code: 'd455', description: 'Sich auf andere Weise fortbewegen' },
        { code: 'b235', description: 'Vestibulärfunktion' },
        { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
      ],
    },
  ],
  icfCategories: [
    { code: 'd450', description: 'Gehen' },
    { code: 'd455', description: 'Sich auf andere Weise fortbewegen' },
    { code: 'b235', description: 'Vestibulärfunktion' },
    { code: 'b760', description: 'Funktionen der Kontrolle von Willkürbewegungen' },
  ],
  calculationType: CalculationType.Sum,
  showPercentFulfillment: true,
  showICFScore: true,
  requiresPatientData: false,
  sideType: SideType.None,
};
